import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';

const SHORTCUT_MAP: Record<string, string> = {
  d: '/dashboard',
  o: '/service-orders',
  e: '/team',
  f: '/finance',
  i: '/inventory',
  s: '/settings',
};

function isInputFocused(): boolean {
  const tag = document.activeElement?.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((document.activeElement as HTMLElement)?.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const pendingG = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isInputFocused()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      // Direct shortcut: N -> Nova OS
      if (key === 'n' && !pendingG.current) {
        e.preventDefault();
        navigate({ to: '/service-orders/new' });
        return;
      }

      // G + X pattern
      if (key === 'g' && !pendingG.current) {
        e.preventDefault();
        pendingG.current = true;
        timer.current = setTimeout(() => {
          pendingG.current = false;
        }, 1000);
        return;
      }

      if (pendingG.current) {
        pendingG.current = false;
        if (timer.current) clearTimeout(timer.current);
        const dest = SHORTCUT_MAP[key];
        if (dest) {
          e.preventDefault();
          navigate({ to: dest });
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [navigate]);
}
